import { ProductTypeValue } from '@app/data-stores/ptr/types';
import {
  CurrentConfiguratorValues,
  InventoryBySelector,
} from '@app/pages/i/hooks/usePTRProductConfigurator';
import { DeepReadonly } from '@app/types';
import {
  Box,
  Button,
  ChevronDownBigIcon,
  ChevronUpBigIcon,
  constants,
  Skeleton,
  Text,
} from '@redbubble/design-system';
import Form from '@redbubble/design-system/dist/Form';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import Color from 'color';
import { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../CollapsibleColorPicker/CollapsibleColorPicker.module.scss';
import { LoadingBoundary } from '../../LoadingBoundary';
import { ColorOption } from '../../ProductConfigurator/FieldConfigurators/types';
import { DSFormOnChangeCallback } from '../../ProductConfigurator/types';
import { useConfiguratorViewportProfile } from '../../ProductConfigurator/useConfiguratorViewportProfile';
import { usePrefersReducedMotion } from '../../ProductConfigurator/usePrefersReducedMotion';
import { PTRProductConfiguratorFields } from '..';
import { ConfiguratorOption, getColorOptionConfig } from '../utils';

type SwatchesResult = {
  swatches: ColorOption[];
  numberOfOtherAvailableColors: number;
};

export const messages = defineMessages({
  colors: {
    defaultMessage: `{availableColors, plural,
        =0 {see color}
        one {+{availableColors} color}
        other {+{availableColors} colors}}
      `,
  },
  colorsButtonAriaLabel: {
    defaultMessage: `See {availableColors, plural,
      one {{availableColors} available color}
      other {{availableColors} available colors}}
    `,
  },
});

export interface PTRCollapsibleColorPickerProps {
  colorConfiguratorOption: ConfiguratorOption;
  productType: DeepReadonly<ProductTypeValue>;
  currentValues: CurrentConfiguratorValues;
  inventoryBySelector: InventoryBySelector;
  onConfiguratorFormChange: DSFormOnChangeCallback;
  locale: string;
}

const getHexString = (hexColor: string): string =>
  hexColor.startsWith('#') ? hexColor : `#${hexColor}`;

const isLight = (hexColor: string) => {
  return Color(getHexString(hexColor)).luminosity() > 0.8;
};

const MAX_SWATCHES = 4;

const getSwatches = (
  currentValue: string,
  colorsData: ColorOption[]
): SwatchesResult => {
  const currentColorData = colorsData.find(
    (color) => color.value === currentValue
  );

  const otherColorsData = colorsData.filter(
    (color) => color.value !== currentValue
  );

  let swatches: ColorOption[];
  if (currentColorData) {
    swatches =
      colorsData.length > MAX_SWATCHES
        ? [currentColorData, ...otherColorsData.slice(0, MAX_SWATCHES - 1)]
        : [currentColorData];
  } else {
    swatches = colorsData.slice(0, MAX_SWATCHES);
  }

  const numberOfOtherAvailableColors = colorsData.length - swatches.length;

  return { swatches, numberOfOtherAvailableColors };
};

export const PTRCollapsibleColorPicker: React.FC<
  PTRCollapsibleColorPickerProps
> = ({
  colorConfiguratorOption,
  currentValues,
  onConfiguratorFormChange,
  locale,
}) => {
  const intl = useIntl();
  const collapsibleRef = useRef<HTMLDivElement>(null);
  const [collapsibleOpen, setCollapsibleOpen] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  const currentValue = useMemo(
    () =>
      currentValues.prefixedFamilyOptionsValues[colorConfiguratorOption.key] ||
      currentValues.prefixedProductOptionsValues[colorConfiguratorOption.key] ||
      '',
    [currentValues, colorConfiguratorOption]
  );
  const initialFormValues = useMemo(
    () => ({ [colorConfiguratorOption.key]: currentValue }),
    [colorConfiguratorOption, currentValue]
  );

  useEffect(() => {
    if (collapsibleRef.current && collapsibleOpen) {
      collapsibleRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'center',
      });
    }
  }, [collapsibleOpen, prefersReducedMotion]);

  const handleCollapsibleClick = () => {
    setCollapsibleOpen(!collapsibleOpen);
  };

  const viewportProfile = useConfiguratorViewportProfile();

  const colorOptions = useMemo((): ColorOption[] => {
    return colorConfiguratorOption.filterValues
      .map(({ value }) => getColorOptionConfig(value, locale))
      .filter((opt) => opt !== null);
  }, [colorConfiguratorOption.filterValues, locale]);

  const { swatches, numberOfOtherAvailableColors } = getSwatches(
    currentValue,
    colorOptions
  );

  const availableColorsMessage = intl.formatMessage(messages.colors, {
    availableColors: numberOfOtherAvailableColors,
  });

  const colorsButtonAriaLabel = intl.formatMessage(
    messages.colorsButtonAriaLabel,
    {
      availableColors: colorOptions.length,
    }
  );

  const getCollapsibleClasses = () => {
    const baseClasses = [styles.collapsibleColorsSection];
    if (collapsibleOpen) {
      baseClasses.push(styles.open);
    }
    if (!prefersReducedMotion) {
      baseClasses.push(styles.animated);
    }
    return baseClasses.join(' ');
  };

  return (
    <LoadingBoundary
      loadingComponent={<Skeleton height={32} width={191} animated />}
    >
      <Button
        type="button"
        intent={constants.PRIMARY}
        size={constants.SMALL}
        onClick={handleCollapsibleClick}
        iconAfter={
          collapsibleOpen ? <ChevronUpBigIcon /> : <ChevronDownBigIcon />
        }
        aria-expanded={!collapsibleOpen}
        aria-label={colorsButtonAriaLabel}
        data-testid="collapsible-color-picker"
      >
        <Box display="flex" paddingRight="xs" ref={collapsibleRef}>
          {swatches.map(({ hexColor }) => (
            <Box
              key={hexColor}
              className={styles.colorSwatch}
              style={{
                backgroundColor: getHexString(hexColor),
                border: isLight(getHexString(hexColor))
                  ? `2px solid ${constants.COLOR_NEUTRAL_200}`
                  : 'none',
              }}
              data-testid="color-swatch"
            />
          ))}
        </Box>
        <Text type="display6">{availableColorsMessage}</Text>
      </Button>

      <Box className={getCollapsibleClasses()}>
        <Form
          enableReinitialize
          initialValues={initialFormValues}
          onChange={onConfiguratorFormChange}
          profile={viewportProfile}
        >
          {({ errors }) => (
            <PTRProductConfiguratorFields
              configuratorOptions={[colorConfiguratorOption]}
              currentValues={currentValues}
              locale={locale}
              errors={errors}
            />
          )}
        </Form>
      </Box>
    </LoadingBoundary>
  );
};
